import { useEffect, useMemo, useState } from 'react'
import { useTranslate } from '@/utils/translate/translate-client'
import { OptionGroup } from '../../components/OptionGroup'
import {
  TorchApplicationGenre,
  TorchApplicationTypes,
  useTorchApplication,
} from '../../components/TorchApplicationContext'

type Props = {
  disabled?: boolean
  hasVideo?: boolean
}

export const TorchUploadCheckboxes = ({ disabled, hasVideo }: Props) => {
  const { data, updateData } = useTorchApplication()
  const [value, setValue] = useState<string[]>([])

  const torchAcknowledgement = data?.uploadAcknowledgements?.includes('torch')
  const { options, label } = useTorchOptions()

  useEffect(() => {
    if (torchAcknowledgement) {
      setValue(
        Array(options.length)
          .fill('0')
          .map((_, index) => `${index}`),
      )
    }
  }, [torchAcknowledgement, options.length])

  useEffect(() => {
    if (disabled) return

    if (!torchAcknowledgement && value.length === options.length) {
      updateData({ uploadAcknowledgements: (data?.uploadAcknowledgements || []).concat(['torch']) })
    } else if (torchAcknowledgement && value.length < options.length) {
      updateData({ uploadAcknowledgements: (data?.uploadAcknowledgements || []).filter((ack) => ack !== 'torch') })
    }
  }, [value, updateData, torchAcknowledgement, data?.uploadAcknowledgements, options, disabled])

  return (
    <OptionGroup
      hasAsset={hasVideo}
      value={value}
      options={options}
      onChange={setValue}
      label={label}
      name="uploadCheckboxes"
      wrap={false}
      outline={false}
      disabled={disabled}
    />
  )
}

const useTorchOptions = () => {
  const { t } = useTranslate('guild-create')
  const { data, type } = useTorchApplication()
  const isFullFilm = type === TorchApplicationTypes.FullFilm
  const isDocOrReality =
    data?.genres?.includes(TorchApplicationGenre.Documentary) || data?.genres?.includes(TorchApplicationGenre.Reality)
  const isKidsGenre = data?.genres?.includes(TorchApplicationGenre.Children)

  const adultOptions = useMemo(
    () => [
      t('torchIsAtLeast5Minutes', 'The Torch is at least 5 minutes in length.'),
      t(
        'torchDoesNotIncludeFWordsOrNudityOrSexualActs',
        'The Torch does not include any F-words, nudity, or sexual acts carried out on screen',
      ),
      t('torchIsNotATrailer', 'The Torch is not a trailer and does not have a trailer as part of it.'),
      t(
        'torchAccuratelyRepresentsTheStylePacingFormatFinalProduction',
        'The Torch accurately represents the style, pacing, and format of the final production.',
      ),
      isDocOrReality
        ? t(
            'torchDocDoesNotHaveCreditsNamesWebsiteLogos',
            'The Torch does not have any credits, personal or company names (the names of the host or participants in a documentary or reality show are okay to be shown), company logos, websites, or QR codes.',
          )
        : t(
            'torchDoesNotHaveCreditsNamesWebsiteLogos',
            'The Torch does not have any credits, personal names, company names, websites, or logos.',
          ),
      t('torchDoesNotIncludeSpeeches', 'The Torch does not include any speeches from the filmmaker.'),
      t(
        'torchDescriptionReferenceAwardsPeopleShows',
        'The Torch and its description do not reference any awards, people, or other shows or film productions.',
      ),
    ],
    [t, isDocOrReality],
  )

  const kidsOptions = useMemo(
    () => [
      t('twoOrFiveMinutes', 'The Torch is 2+ minutes if animated, or 5+ minutes if animatic or live action.'),
      t(
        'kidsTorchNoCrudeLanguage',
        'The Torch does not use any crude or inappropriate language and does not have any sexual references.',
      ),
      t('kidsTorchNoDrugsAlcohol', 'There are no references to drugs, alcohol, or harmful substances.'),
      t(
        'kidsTorchNoHarmfulLanguage',
        'There are no other depictions or language that could be seen as harmful or too mature for younger children.',
      ),
      t('kidsTorchIsNotATrailer', 'The Torch is not a trailer and does not lead with a trailer.'),
      t(
        'kidsTorchRepresentsStylePacingFormat',
        'The Torch accurately represents the style, pacing, and format of the final production.',
      ),
      t(
        'kidsTorchDoesNotHaveCreditsNamesWebsiteLogos',
        'The Torch does not have any credits, personal or company names, company logos, websites, or QR codes.',
      ),
      t(
        'kidsTorchDoesNotIncludeSpeeches',
        'The Torch does not include any explainer videos from the filmmaker or cast.',
      ),
      t(
        'kidsTorchDescriptionReferenceAwardsPeopleShows',
        'The Torch and its description do not reference any awards, people, or other film productions.',
      ),
    ],
    [t],
  )

  const fullFilmOptions = useMemo(
    () => [
      t('filmDoesNotIncludeFWordsOrNudity', 'The film does not include any F-words.'),
      t(
        'filmDoesNotIncludeNudityOrSexualActs',
        'The film does not include any nudity or sexual acts carried out on screen.',
      ),
      t('filmDoesNotIncludeSpeeches', 'The film does not include any explainer videos from the filmmaker or cast.'),
    ],
    [t],
  )

  const fullFilmKidsOptions = useMemo(
    () => [
      t(
        'kidsFilmNoCrudeLanguage',
        'The film does not use any crude or inappropriate language and does not have any sexual references.',
      ),
      t('kidsFilmNoDrugsAlcohol', 'The film does not have any references to drugs, alcohol, or harmful substances.'),
      t(
        'kidsFilmNoHarmfulLanguage',
        'The film does not have depictions or language that could be seen as harmful or too mature for younger children.',
      ),
      t('kidsFilmDoesNotIncludeSpeeches', 'The film does not include any explainer videos from the filmmaker or cast.'),
    ],
    [t],
  )

  const kidsLabel = t(
    'toFitIntoChildrensGenreIAcknowledgeThatMyTorchMeetsTheFollowingCriteria',
    "To fit into the Children's genre, I acknowledge that my Torch meets the following criteria:",
  )
  const kidsLabelFullFilm = t(
    'toFitIntoChildrensGenreIAcknowledgeThatMyFilmMeetsTheFollowingCriteria',
    "To fit into the Children's genre, I acknowledge that my film meets the following criteria:",
  )
  const adultLabel = t(
    'iAcknowledgeThatMyTorchMeetsTheFollowingCriteria',
    'I acknowledge that my Torch meets the following criteria:',
  )
  const adultLabelFullFilm = t(
    'iAcknowledgeThatMyFilmMeetsTheFollowingCriteria',
    'I acknowledge that my film meets the following criteria:',
  )

  return useMemo(() => {
    if (isFullFilm) {
      return {
        options: (isKidsGenre ? fullFilmKidsOptions : fullFilmOptions).map((label, index) => ({
          label,
          value: `${index}`,
        })),
        label: isKidsGenre ? kidsLabelFullFilm : adultLabelFullFilm,
      }
    }

    return {
      options: (isKidsGenre ? kidsOptions : adultOptions).map((label, index) => ({
        label,
        value: `${index}`,
      })),
      label: isKidsGenre ? kidsLabel : adultLabel,
    }
  }, [
    isFullFilm,
    isKidsGenre,
    kidsOptions,
    adultOptions,
    kidsLabel,
    adultLabel,
    fullFilmKidsOptions,
    fullFilmOptions,
    kidsLabelFullFilm,
    adultLabelFullFilm,
  ])
}
